import React, { useEffect, useState, useMemo, useRef } from "react";
import IntlMessages from "util/IntlMessages";
import { Card, Table, Row, Col, Select, Button, Result } from "antd";
import { useDispatch, useSelector } from "react-redux";
import ReportActionButtons from "components/ReportActionButtons";
import CustomPagination from "components/CustomPagination";
import {
  downloadFilingReport,
  getAdminFilingPlansReport,
} from "../../../appRedux/reducers/FilingPlansSlice";
import LoadingBar from "../../../components/LoadingBar";

const CustomFilterDropdown = ({
  options,
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  placeholder = "Search..."
}) => (
  <div style={{ padding: 8, width: 280 }}>
    <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
      <Select
        mode="multiple"
        placeholder={placeholder}
        value={selectedKeys}
        onChange={setSelectedKeys}
        showSearch
        style={{
          width: '100%',
          minHeight: 20,
          marginBottom: 0,
          display: 'block'
        }}
        filterOption={(input, option) =>
          option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
        maxTagCount="responsive"
        allowClear={false}
        dropdownRender={menu => (
          <>
            {menu}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 8,
              padding: '0 8px'
            }}>
              <Button
                size="small"
                onClick={() => {
                  clearFilters && clearFilters();
                  confirm && confirm();
                }}
                style={{ flex: 1 }}
              >
                Reset
              </Button>
              <Button
                type="primary"
                size="small"
                onClick={() => confirm && confirm()}
                style={{ flex: 1 }}
              >
                Apply
              </Button>
            </div>
          </>
        )}
      >
        {options.map(option => (
          <Select.Option key={option.value} value={option.value}>
            {option.text}
          </Select.Option>
        ))}
      </Select>
    </div>
    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
      <Button
        size="small"
        style={{ flex: 1 }}
        onClick={() => {
          clearFilters && clearFilters();
          confirm && confirm();
        }}
      >
        Reset
      </Button>
      <Button
        type="primary"
        size="small"
        style={{ flex: 1 }}
        onClick={() => confirm && confirm()}
      >
        Apply
      </Button>
    </div>
  </div>
);

const FilingPlansReport = () => {
  const dispatch = useDispatch();
  const { isLoaded, plansReportData, reportError } = useSelector(({ filingPlans }) => filingPlans);
  const fetchAbortRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
  });
  const [requestObj, setRequestObj] = useState({
    limit: 50,
    offset: 0,
  });
  const [quarterDropdownCurrent, setQuarterDropdownCurrent] = useState("");
  const [currentFilters, setCurrentFilters] = useState({});
  const [currentSorter, setCurrentSorter] = useState({});

  // Normalize quarter format for consistency
  const normalizeQuarter = (q) => {
    if (!q) return q;
    if (q.includes("Jan")) return q.replace(/Jan.*(\d{4})/, "Jan-Mar $1");
    if (q.includes("Apr")) return q.replace(/Apr.*(\d{4})/, "Apr-Jun $1");
    if (q.includes("Jul")) return q.replace(/Jul.*(\d{4})/, "Jul-Sep $1");
    if (q.includes("Oct")) return q.replace(/Oct.*(\d{4})/, "Oct-Dec $1");
    return q;
  };

  // Initialize with current quarter from API
  useEffect(() => {
    if (plansReportData?.current_quarter) {
      setQuarterDropdownCurrent(normalizeQuarter(plansReportData.current_quarter));
    }
  }, [plansReportData?.current_quarter]);

  // Fetch data when request parameters change
  useEffect(() => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    const params = { ...requestObj };
    if (quarterDropdownCurrent) {
      params.quarter = normalizeQuarter(quarterDropdownCurrent);
    }
    dispatch(getAdminFilingPlansReport({ data: params, signal: controller.signal }));

    return () => controller.abort();
  }, [requestObj, dispatch]);

  const handleRetry = () => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    const params = { ...requestObj };
    if (quarterDropdownCurrent) {
      params.quarter = normalizeQuarter(quarterDropdownCurrent);
    }
    dispatch(getAdminFilingPlansReport({ data: params, signal: controller.signal }));
  };

  // Create memoized columns to prevent unnecessary re-renders
  const columns = useMemo(() => {
    if (!isLoaded || !plansReportData) return [];

    const { filters = {}, product_order = [] } = plansReportData;
    const { partner_filter = [], country_filter = [], status_filter = [] } = filters;
    const allProductColumns = product_order || [];

    // Base columns (company name and country)
    const baseColumns = [
      {
        title: "Company Name",
        dataIndex: "company_name",
        key: "company_name",
        fixed: "left",
        render: name => <div className="text-capitalize">{name || '-'}</div>,
        width: 240,
        filteredValue: currentFilters.company_name || null,
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
          <CustomFilterDropdown
            options={partner_filter.map(partner => ({
              text: partner.company_name?.replace(
                /(^\w{1})|(\s{1}\w{1})/g,
                match => match.toUpperCase()
              ) || '-',
              value: partner.partner_id,
            }))}
            selectedKeys={selectedKeys}
            setSelectedKeys={setSelectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            placeholder="Search companies..."
          />
        ),
        filterMultiple: true,
        onHeaderCell: () => ({
          style: {
            zIndex: 20,
            backgroundColor: '#fff',
          },
        }),
      },
      {
        title: "Country",
        dataIndex: "country",
        key: "country",
        fixed: "left",
        render: name => <div className="text-capitalize">{name || '-'}</div>,
        width: 240,
        filteredValue: currentFilters.country || null,
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
          <CustomFilterDropdown
            options={country_filter.map(country => ({
              text: country.country_name || '-',
              value: country.country_id,
            }))}
            selectedKeys={selectedKeys}
            setSelectedKeys={setSelectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            placeholder="Search countries..."
          />
        ),
        filterMultiple: true,
        onHeaderCell: () => ({
          style: {
            zIndex: 20,
            backgroundColor: '#fff',
          },
        }),
      },
    ];

    // Product columns
    const productColumnDefs = allProductColumns.map(productName => ({
      title: productName,
      dataIndex: productName,
      key: productName,
      width: 240,
      render: (status, record) => {
        return record[productName] ? (
          <div className="text-capitalize">{record[productName]}</div>
        ) : null;
      },
      filteredValue: currentFilters[productName] || null,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <CustomFilterDropdown
          options={status_filter.map(status => ({
            text: status || '-',
            value: status,
          }))}
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          placeholder="Search status..."
        />
      ),
      filterMultiple: true,
    }));

    return [...baseColumns, ...productColumnDefs];
  }, [isLoaded, plansReportData, currentFilters]);

  // Process API data to create table rows
  useEffect(() => {
    if (isLoaded && plansReportData) {
      const { results = [], count = 0, product_order = [] } = plansReportData;
      const allProductColumns = product_order || [];

      // Transform rows to ensure all product columns exist in each row
      const transformedRows = results.map(row => {
        const newRow = { ...row };
        allProductColumns.forEach(product => {
          if (!(product in newRow)) {
            newRow[product] = null;
          }
        });
        return newRow;
      });

      setRows(transformedRows);
      setTotal(count);
    }
  }, [isLoaded, plansReportData]);

  // Handle table changes (filtering, sorting, pagination)
  const handleTableChange = (paginationInfo, filters, sorter) => {
    // Update current filters and sorter state
    setCurrentFilters(filters);
    setCurrentSorter(sorter);

    // Prepare filter params for API
    const filterParams = {
      partner_id: filters.company_name,
      country_id: filters.country,
      product_status_dict: Object.fromEntries(
        Object.entries(filters).filter(([key]) => !['company_name', 'country'].includes(key))
      ),
    };

    // Reset to first page when filters change
    const newPagination = { ...pagination, current: 1 };
    setPagination(newPagination);

    setRequestObj(prev => ({
      ...prev,
      ...filterParams,
      limit: newPagination.pageSize,
      offset: 0,
      sort_field: sorter.field,
      sort_order: sorter.order,
    }));
  };

  // Handle pagination changes
  const handlePaginationChange = (page, pageSize) => {
    const offset = (page - 1) * pageSize;
    const newPagination = { current: page, pageSize };
    setPagination(newPagination);
    setRequestObj(prev => ({
      ...prev,
      limit: pageSize,
      offset,
    }));
  };

  // Handle quarter dropdown change
  const changeQuarter = (quarter) => {
    const normalizedQuarter = normalizeQuarter(quarter);
    setQuarterDropdownCurrent(normalizedQuarter);
    
    // Reset to first page but keep current page size
    const newPagination = { ...pagination, current: 1 };
    setPagination(newPagination);

    setRequestObj({
      limit: newPagination.pageSize,
      offset: 0,
      quarter: normalizedQuarter,
      // Preserve existing filters and sorting
      partner_id: currentFilters.company_name,
      country_id: currentFilters.country,
      product_status_dict: Object.fromEntries(
        Object.entries(currentFilters).filter(([key]) => !['company_name', 'country'].includes(key))
      ),
      sort_field: currentSorter.field,
      sort_order: currentSorter.order,
    });
  };

  // Handle download
  const handleDownload = () => {
    dispatch(
      downloadFilingReport({
        filename: "filing-plans.csv",
        quarter: normalizeQuarter(quarterDropdownCurrent),
        params: {
          ...requestObj,
          quarter: normalizeQuarter(quarterDropdownCurrent),
        },
      })
    );
  };

  return (
    <div className="gx-main-content">
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <div className="gx-flex-row gx-align-items-center gx-justify-content-between gx-mb-4">
              <h1 className="title">
                <IntlMessages id="report.filing-plans" />
              </h1>

              <ReportActionButtons
                quarterDropdownCurrent={quarterDropdownCurrent}
                onQuarterChange={changeQuarter}
                quarterDropdown={(plansReportData?.quarter_dropdown || []).map(normalizeQuarter)}
                onDownload={handleDownload}
                isUserAdmin={false}
                isUserStaff={false}
                isHistoricQuarter={false}
                reportStatus=""
                messageCount={0}
                messages={[]}
                onNotificationClick={() => {}}
                showSave={false}
                showApprove={false}
                showReject={false}
                showSubmit={false}
                showDownload={true}
                showQuarterDropdown={true}
                showMessages={false}
              />
            </div>

            {!isLoaded ? (
              <LoadingBar loading={true} />
            ) : reportError ? (
              <Result
                status="error"
                title="Failed to load Filing Plans report"
                subTitle={reportError}
                extra={
                  <Button type="primary" onClick={handleRetry}>
                    Retry
                  </Button>
                }
              />
            ) : (
              <>
              <div style={{ 
                  height: 'calc(100vh - 250px)',
                  display: 'flex', 
                  flexDirection: 'column' 
                }}>
                <Table
                  className="gx-table-responsive"
                  style={{ flex: 1 }}
                  columns={columns.map(col => ({
                    ...col,
                    onCell: () => ({
                      style: { textAlign: 'center', verticalAlign: 'middle' }
                    })
                  }))}
                  dataSource={rows}
                  pagination={false}
                  scroll={{ x: 'max-content', y: 'calc(100vh - 300px)' }}
                  size="middle"
                  onChange={handleTableChange}
                  bordered
                  loading={!isLoaded}
                  rowKey={(record) => `${record.company_name}-${record.country}`}
                  components={{
                    header: {
                      cell: (props) => (
                        <th
                          {...props}
                          style={{
                            ...props.style,
                            backgroundColor: '#fff',
                            position: 'sticky',
                            top: 0,
                          }}
                        />
                      ),
                    },
                  }}
                />
                </div>
                <div style={{ 
                  marginTop: '24px', 
                }}>
                <CustomPagination
                  current={pagination.current}
                  total={total}
                  pageSize={pagination.pageSize}
                  showSizeChanger={true}
                  pageSizeOptions={[10, 20, 50, 100]}
                  onChange={handlePaginationChange}
                  onShowSizeChange={handlePaginationChange}
                />
                </div>
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FilingPlansReport;